import {useQueryClient} from '@tanstack/react-query';

import {
	CURRENT_ROUTE_KEY,
	resolveDeferred,
	SEO_DATA_KEY,
	useLocalesMgr,
	useLocation,
} from 'coreHooks';
import {buildDeferredRoute} from 'coreHooks/resolvePageDeferreds';
import {receiveRoute, selectRoute} from 'coreStore/data/page';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

interface PageInitSafetyNetProps {
	templateName: string;
}

/**
 * Fallback that resolves the `current-route` deferred for pages that don't
 * call `usePageInit` themselves.
 *
 * Does NOT resolve `seo-data` — that is handled by `usePageInit`'s
 * synchronous render body which sets Redux metadata and resolves the
 * deferred in the same pass, guaranteeing `SuspenseSEO` renders with
 * the correct title.
 */
const PageInitSafetyNet = ({templateName}: PageInitSafetyNetProps) => {
	const qc = useQueryClient();
	const {managerRepository} = useGeneralProvider();
	const location = useLocation();
	const localesMgr = useLocalesMgr();

	const routeDeferredMissing = qc.getQueryData([CURRENT_ROUTE_KEY]) == null;
	const seoDeferredMissing = qc.getQueryData([SEO_DATA_KEY]) == null;

	if (!routeDeferredMissing && !seoDeferredMissing) {
		return null;
	}

	const urlSlug = localesMgr.getUrlWithoutLocale(location.pathname);
	const isHomePage = urlSlug === '/' || urlSlug === '';
	const store = managerRepository.getStoreMgr().getStore();
	const routeState = selectRoute(store.getState());
	const currentRoute = routeState.currentRoute ?? templateName;
	const resolvedIsHomePage = routeState.currentRoute != null ? routeState.isHomePage : isHomePage;

	if (routeState.currentRoute == null) {
		store.dispatch(receiveRoute({currentRoute, isHomePage: resolvedIsHomePage}));
	}

	if (routeDeferredMissing) {
		resolveDeferred(qc, CURRENT_ROUTE_KEY, buildDeferredRoute(currentRoute, resolvedIsHomePage));
	}

	return null;
};

export default PageInitSafetyNet;
